import React, { ReactNode } from "react";
import Skeleton from "react-loading-skeleton";
import { Link, useNavigate } from "react-router-dom";
import {
	Badge,
	Box,
	ButtonProps,
	Center,
	Container,
	Flex,
	Group,
	Indicator,
	Paper,
	ScrollArea,
	SimpleGrid,
	Stack,
	Tabs,
	Text,
	Title,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { parseAsString, useQueryState } from "nuqs";

import { openContactUsForm } from "@/components/ContactUsForm/ContactUsForm";
import { GreenWaves } from "@/components/GreenWaves";
import { ThemedButton, ThemedCard } from "@/components/ThemedButton";

import authApi from "@/services/auth-api";

import { AVAILABLE_COURSES_LINKS } from "@/utilities/constant";

import CertExample from "./assets/cert-example.png";
import CertListingImage from "./assets/cert-listing-image.jpeg";
import CertOfCompletionExample from "./assets/cert-of-completion-sample.png";
import { ReactComponent as TrophySvg } from "./assets/trophy.svg";
import { ReactComponent as UserDigitalSkill } from "./assets/user-digital-skill.svg";
import { ReactComponent as UserSkillGer } from "./assets/user-skill-gear.svg";
import WSQExample from "./assets/wsq-cert-vertor.png";
import CertBadgeIcon from "./components/CertBadge";
import { CertFolderAnimation } from "./components/CertFolderAnimation";
import CertPreview from "./components/CertPreview";
import styles from "./ListingPage.module.scss";

import "react-loading-skeleton/dist/skeleton.css";

export interface IndividualCertData {
	cert_id: number;
	course_title: string;
	trainer_name: string;
	course_ref: string;
	serial_no: string;
	issue_at: string;
	courserun_course_from: string;
	courserun_course_to: string;
	course_category_type?: "digital" | "softskills";
	cert_url: string;
	cert_image_url: string;
	verification_url: string | null;
}

export interface BundleCertData {
	bundle_id: string;
	bundle_name: string;
	sample_cert_image_url: string;
	course_count: number;
	cert_id: string | null;
	certData: {
		cert_image_url: string;
		cert_courses_count: number;
		cert_url: string | null;
		cert_courses: Array<{
			course_id: string;
			courserun_id: string;
			issue_date: string;
			verification_url: string | null;
			course_title: string;
			trainer_name: string;
			course_ref: string;
			learner_id: null;
			is_completed: boolean;
			courserun_course_from: string;
			courserun_course_to: string;
		}>;
	};
	registered_courses: Array<{
		course_id: string;
		course_title: string;
		course_ref: string;
		courserun_id: string;
		attendance_status: string;
		courserun_course_from: string;
		courserun_course_to: string;
		is_completed: boolean;
		trainer_name: string;
	}>;
	further_action_text?: string;
	further_action_course_list?: {
		course_id: string;
		course_title: string;
	}[];
	can_view: boolean;
	can_checkout: boolean;
	can_customize: boolean;
}

export function ListingPage() {
	const [activeTab, setActiveTab] = useQueryState(
		"tab",
		parseAsString.withDefault("individual_certs"),
	);

	const navigate = useNavigate();

	const query = useQuery({
		queryKey: ["certification"],
		queryFn: () => {
			return authApi.getProfileBundleV2() as Promise<{
				past_bundle_cert: BundleCertData[];
				current_bundles: BundleCertData[];
				individual_certs: IndividualCertData[];
				wsq_cert_html: string;
				wsq_cert_url: string;
				career_skill_passport_url: string;
				individual_cert_preview?: string;
				professional_cert_preview?: string;
			}>;
		},
	});

	const allBundleCerts = [
		...(query.data?.current_bundles || []),
		...(query.data?.past_bundle_cert || []),
	];

	const showSkeleton = query.isFetching && !query.isFetched;

	return (
		<div>
			<Banner />
			<Box id="cert-section">
				<Tabs defaultValue="current" value={activeTab} bg="white" pt={100} pb={100}>
					<Flex
						gap={0}
						justify="center"
						direction={{ base: "column", md: "row" }}
						bd="1.5px solid var(--mantine-color-themed-2)"
						p={10}
						mb={50}
						bdrs={"md"}
						bg="white"
						w="fit-content"
						mx="auto">
						<TabButton
							onClick={() => setActiveTab("individual_certs")}
							active={activeTab === "individual_certs"}
							miw={280}>
							Certificate of Completion{" "}
							{query.data?.individual_certs?.length
								? `(${query.data?.individual_certs?.length})`
								: ""}
						</TabButton>
						<TabButton
							onClick={() => setActiveTab("current")}
							active={activeTab === "current"}
							miw={280}>
							Professional Certificate {allBundleCerts.length ? `(${allBundleCerts.length})` : ""}
						</TabButton>
						<TabButton onClick={() => setActiveTab("wsq")} active={activeTab === "wsq"} miw={200}>
							WSQ Certificate
						</TabButton>
					</Flex>

					<div></div>
					<Tabs.Panel value="individual_certs">
						<Container size={"md"} my={20}>
							{showSkeleton && <IndividualCertSkeleton />}
							{query.isFetched && query.data?.individual_certs?.length === 0 && (
								<IndividualCertEmptyPlaceholder
									key={activeTab}
									previewUrl={query.data.individual_cert_preview}
								/>
							)}
							<SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
								{query.data?.individual_certs?.map((cert, index) => {
									return (
										<IndividualCertCard
											cert={cert}
											cert_image_url={cert.cert_image_url}
											badge
											shimmer={false}
											delay={(index % 3) * 0.2}></IndividualCertCard>
									);
								})}
							</SimpleGrid>
						</Container>
					</Tabs.Panel>

					<Tabs.Panel value="wsq">
						<Container size={"md"} pb={40}>
							<ThemedCard py={40} px={{ base: 10, md: 30 }} mb={50} mt={20}>
								<Flex
									w="100%"
									direction={{ base: "column", md: "row" }}
									align="center"
									justify="center"
									my={20}
									gap={20}>
									<Flex flex={"0 1 45%"}>
										<CertFolderAnimation key={activeTab} previewImage={WSQExample} />
									</Flex>
									<Box ml={{ base: 0, md: 10 }} flex="1 1 200px">
										<Title
											order={2}
											mb={20}
											ta={{
												base: "center",
												md: "left",
											}}>
											To retrieve your WSQ certificate
										</Title>
										<div>
											<strong>Step 1:</strong> Log in to MySkillsFuture with Singpass, go to Careers
											& Skills Passport
											<br />
											<br />
											<strong>Step 2:</strong> Select Certificates (or Qualifications &
											Certificates)
											<br />
											<br />
											<strong>Step 3:</strong> Select your WSQ SOA/Qualification to download the
											e-Cert.
											<br />
											<br />
											<strong>Note:</strong> From 1 Jul 2024, new WSQ certificates are issued as
											OpenCerts, which you can view/download from the same section. For learners
											without Singpass (e.g., foreigners), use the Download Certificates e-service
											for foreigners or ask your training provider for help.
										</div>
										<Group mt={20}>
											<ThemedButton
												component={"a"}
												href={query.data?.wsq_cert_url || ""}
												target="_blank">
												WSQ
											</ThemedButton>
											<ThemedButton
												component={"a"}
												href={query.data?.career_skill_passport_url || ""}
												target="_blank"
												variant="outline">
												Careers & Skills Passport
											</ThemedButton>
										</Group>
									</Box>
								</Flex>
							</ThemedCard>
						</Container>
					</Tabs.Panel>

					<Tabs.Panel value="current">
						{query.isFetched && !Boolean(query.data?.current_bundles.length) ? (
							<Container size={"md"}>
								<BundleCertEmptyPlaceholder
									key={activeTab}
									previewUrl={query.data?.professional_cert_preview}
								/>
							</Container>
						) : null}

						{/* <Container size={'md'}>
							<BundleCertEmptyPlaceholder previewUrl={query.data?.professional_cert_preview} />
						</Container> */}

						<Container py={40}>
							{showSkeleton && <BundleCertSkeleton />}
							<Stack gap={40} align="center">
								{allBundleCerts.map((bundle) => {
									return (
										<BundleCertCard
											data={bundle}
											sample_cert_image_url={
												String(bundle.bundle_id) == "16"
													? query.data?.professional_cert_preview || CertExample
													: null
											}
										/>
									);
								})}
							</Stack>
						</Container>
					</Tabs.Panel>
				</Tabs>
			</Box>
		</div>
	);
}

export const IndividualCertCard = (props: {
	cert: IndividualCertData;
	cert_image_url: string;
	badge?: boolean;
	children?: ReactNode;
	shimmer?: boolean;
	delay?: number;
}) => {
	const navigate = useNavigate();
	const { cert, badge, cert_image_url } = props;
	const params = new URLSearchParams();
	params.set("cert_id", cert.cert_id);
	const search = params.toString();
	// navigate(`/certification/view-cert?${search}`)
	return (
		<motion.div
			initial={{ opacity: 0, y: 50 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true }}
			transition={{
				duration: 0.5,
				ease: "easeOut",
				delay: props.delay || 0,
			}}>
			<Link to={`/certification/view-cert?${search}`}>
				<ThemedCard bg="white">
					<Box
						mx={"auto"}
						w={"90%"}
						style={{
							height: 200,
							overflow: "hidden",
							position: "relative",
							background: "#8CF4DC",
							padding: "10px 20px",
							borderRadius: 20,
							marginBottom: 5,
						}}>
						<CertPreview src={cert_image_url} width={"100%"} />
						{badge ? <CertBadgeIcon shimmer={true} /> : null}
					</Box>
					<Flex flexDirection="column" justifyContent="space-between" flex={1}>
						<Box mt={15} gap={0} w="100%">
							<Title order={6} lh={1.2}>
								{cert.course_title}
							</Title>
							<Group wrap="nowrap">
								<Stack flex={"1 1 auto"} gap={0}>
									{cert.trainer_name ? (
										<Text c="gray.7" size="xs" ta="left" lh={1.2}>
											Trainer: {cert.trainer_name}
										</Text>
									) : null}
									<Text c="gray.8" size="xs" ta="left" lh={1.2}>
										{cert.course_ref}
									</Text>
									<Text c="gray.7" size="xs" ta="left" lh={1.2}>
										Date of issue: {cert.issue_at}
									</Text>
									<Text c="gray.7" size="xs" ta="left" lh={1.2}>
										Course Date: {cert.courserun_course_from} to {cert.courserun_course_to}
									</Text>
								</Stack>
								<Box flex={"0 0 auto"}>
									{/* TODO: Need to show the correct icon based on the cert type */}
									{cert.course_category_type === "digital" ? (
										<UserSkillGer width={45} height={45} />
									) : null}
									{cert.course_category_type === "softskills" ? (
										<UserDigitalSkill width={45} height={45} />
									) : null}
								</Box>
							</Group>

							<Group justify="end" mt={10}>
								<ThemedButton
									size="xs"
									onClick={(event) => {
										event.preventDefault();
										event.stopPropagation();
										const params = new URLSearchParams();
										params.set("cert_id", cert.cert_id.toString());
										const search = params.toString();
										navigate(`/certification/view-cert?${search}`);
									}}>
									View
								</ThemedButton>
							</Group>
						</Box>
					</Flex>
				</ThemedCard>
			</Link>
		</motion.div>
	);
};

const BundleCertCard = (props: {
	bg?: string;
	data: BundleCertData;
	sample_cert_image_url: string | null;
}) => {
	const { bg = "#F0FDFF", data } = props;
	const { bundle_name, sample_cert_image_url, course_count, certData, registered_courses } = data;

	const navigate = useNavigate();

	return (
		<ThemedCard
			display={"inline-block"}
			py={40}
			pos="relative"
			w={{
				base: "100%",
				md: "75%",
			}}
			bg={bg}>
			<Flex align="flex-start" wrap="nowrap" direction={{ base: "column", md: "row" }}>
				<Flex justify="center" bg={"transparent"} flex={"0 0 auto"} w={{ base: "100%", md: "45%" }}>
					<Box
						mx={"auto"}
						w={"100%"}
						maw={{
							base: "min(80%, 400px)",
							md: "none",
						}}>
						<CertFolderAnimation
							key="bundle-cert-folder-animation"
							previewImage={
								certData?.cert_image_url ||
								certData?.cert_url ||
								props.sample_cert_image_url ||
								sample_cert_image_url
							}
						/>
					</Box>
				</Flex>
				<Flex
					display="flex"
					direction="column"
					style={{ flexGrow: 1, flexShrink: 1 }}
					mt={{ base: 20, md: 0 }}
					ml={{ base: 0, md: 20 }}>
					<Title order={2} ta={{ base: "center", md: "left" }}>
						{bundle_name}
					</Title>

					{certData ? <Text size="sm">Date Issued: {certData.issue_date} </Text> : null}

					<Flex justify={{ base: "center", md: "start" }} gap={10}>
						{certData ? (
							<Badge
								size="md"
								variant="outline"
								color="#00AB84"
								bdrs={0}
								leftSection={<i className="fa fa-check" />}
								mt={5}
								px={20}>
								Completed
							</Badge>
						) : (
							<Badge
								size="md"
								variant="outline"
								color="#00AB84"
								bdrs={0}
								leftSection={<i className="fa fa-check" />}
								mt={5}
								px={20}>
								{Math.min(Math.round((registered_courses.length / course_count) * 100), 100)}%
								Completed
							</Badge>
						)}
					</Flex>

					<Box component="hr" my={10}></Box>

					<ScrollArea h={250} type="always" w="100%" px={10}>
						<Title order={6} c="black">
							Registered Courses
						</Title>
						<Stack gap={10} mt={10}>
							{!registered_courses?.length ? (
								<Text size="sm" c="gray">
									No courses registered
								</Text>
							) : null}
							{(certData?.cert_courses || registered_courses).map((course) => {
								return (
									<Group gap={10} wrap="nowrap" align="start">
										<Flex flex={"0 0 auto"} align="start">
											{course.is_completed ? (
												<TrophySvg width={13} height={13} />
											) : (
												<Box
													style={{ flexShrink: 0 }}
													bdrs={"100%"}
													w={13}
													h={13}
													bd={"1px solid var(--mantine-color-gray-8)"}
													mt={5}></Box>
											)}
										</Flex>
										<Stack gap={2}>
											<Text size={"sm"} lh={1}>
												{course.course_title}{" "}
												{!course.is_completed ? (
													<Text span c="gray">
														(In Progress)
													</Text>
												) : null}
											</Text>
											<Text size={"sm"} lh={1} c="gray.6">
												{course.course_ref}
											</Text>
											<Text size={"sm"} lh={1} c="gray.6">
												{course.courserun_course_from} to {course.courserun_course_to}{" "}
											</Text>
											{course.trainer_name ? (
												<Text size={"sm"} lh={1} c="gray.6">
													Trainer: {course.trainer_name}
												</Text>
											) : null}
										</Stack>
									</Group>
								);
							})}
						</Stack>
						{Boolean(data.further_action_text) && (
							<Paper bg="transparent" bdrs={0} mt={10}>
								<Text size="sm" py={10} fw="bold">
									{data.further_action_text}
								</Text>
								<Stack gap={5}>
									{data.further_action_course_list?.map((course, index) => {
										return (
											<Group gap={5} wrap="nowrap" align="flex-start">
												<Box
													style={{ flexShrink: 0 }}
													bdrs={"100%"}
													w={10}
													h={10}
													bd={"1px solid var(--mantine-color-gray-8)"}
													mt={5}></Box>
												<Text key={index} size={"sm"}>
													{course.course_title}
												</Text>
											</Group>
										);
									})}
								</Stack>
							</Paper>
						)}
					</ScrollArea>

					<Group mt={40} gap={10} justify="end">
						<ThemedButton
							size="xs"
							leftSection={<i className="fa fa-info-circle" />}
							variant="outline"
							component={Link}
							to={`/course/bundle/${data.bundle_id}`}>
							Courses Available
						</ThemedButton>
						<ThemedButton
							radius={0}
							size="xs"
							px={40}
							disabled={!data.can_view}
							onClick={() => {
								const params = new URLSearchParams();
								params.set("bundle_id", data.bundle_id);
								if (data.cert_id) {
									params.set("cert_id", data.cert_id);
								}
								const search = params.toString();
								navigate(`/certification/view-cert?${search}`);
							}}>
							View
						</ThemedButton>
						{data.can_checkout && (
							<Indicator processing>
								<ThemedButton
									radius={0}
									size="xs"
									px={40}
									onClick={() => {
										navigate(`/professional-cert/create?bundle_id=${data.bundle_id}`);
									}}>
									Complete Your Certificate
								</ThemedButton>
							</Indicator>
						)}
					</Group>
					{data.can_checkout && (
						<Text size="sm" mt={10} ta="right" c="black">
							Congradutlations! You're eligible for this professional certificate. Click the button
							above to complete your certificate.
						</Text>
					)}
				</Flex>
			</Flex>
		</ThemedCard>
	);
};

const BundleCertSkeleton = () => {
	return (
		<Container bg="#edf4f3" w="100%">
			<Box display="flex" flexDirection="row" gap={20} pl={40}>
				<div style={{ width: "65%", height: "100%" }} className="p-4">
					<Skeleton enableAnimation={true} style={{ aspectRatio: 3 / 4 }} width={"100%"} />
				</div>
				<div className="flex flex-column w-100 p-2 pt-4" style={{ flexGrow: 1, flexShrink: 1 }}>
					<Skeleton enableAnimation={true} count={1} height={30} />
					<Skeleton enableAnimation={true} count={1} height={30} width={150} />
					<Skeleton enableAnimation={true} count={1} height={5} />
					<Skeleton enableAnimation={true} count={3} height={30} width={"60%"} />
				</div>
			</Box>
		</Container>
	);
};

const TabButton = (props: ButtonProps & { active?: boolean; onClick?: () => void }) => {
	const { active, children, ...rest } = props;

	if (active) {
		return (
			<ThemedButton
				radius={0}
				variant="gradient"
				gradient={{
					from: "themed.8",
					to: "themed.7",
					deg: 200,
				}}
				{...rest}>
				{props.children}
			</ThemedButton>
		);
	} else {
		return (
			<ThemedButton variant="transparent" color="black" {...rest}>
				{props.children}
			</ThemedButton>
		);
	}
};

const IndividualCertEmptyPlaceholder = ({
	previewUrl = CertOfCompletionExample,
}: {
	previewUrl?: string;
}) => {
	return (
		<ThemedCard px={30} mb={50} mt={20}>
			<Flex
				w="100%"
				direction={{ base: "column", md: "row" }}
				align="center"
				justify="center"
				my={20}
				gap={20}>
				<Flex flex={"0 1 45%"}>
					<CertFolderAnimation key="individual-cert-folder-animation" previewImage={previewUrl} />
				</Flex>
				<Box ml={{ base: 0, md: 10 }} flex="1 1 200px">
					<Title order={2}>Certificates of Completion</Title>
					<br />
					<Flex
						h="100%"
						align="center"
						bg="themed-black.0"
						px={10}
						py={10}
						pt={12}
						bdrs={5}
						gap={10}>
						<Text size="xs">
							<i
								className="fa fa-info-circle"
								style={{ color: "var(--mantine-color-themed-black-7)" }}
							/>
						</Text>
						<Text size="xs">You do not have any certificate at the moment.</Text>
					</Flex>

					<Text size="sm" my={20} lh={1.3}>
						Complete a course and get a verifiable Certificate of Completion that you can add to
						your resume and LinkedIn profile easily.
						<br />
						<br />
						<strong>How to add it to LinkedIn (1 minute)</strong>
						<br />
						<br />
						<strong>Step 1:</strong> Profile → Add profile section → Licenses & Certifications
						<br />
						<strong>Step 2:</strong> Name: Certificate of Completion — [Course Title]
						<br />
						Issuing organization: Skills Development Academy
						<br />
						<strong>Step 3:</strong> Enter the issue date, paste the verification link, and Save
						<br />
						<br />
						<strong>How to verify the Certificate</strong>
						<br />
						<br />
						<strong>Step 1:</strong> Scan QR code on the Certificate
						<br />
						<strong>Step 2:</strong> Verify the Certificate
						<br />
						<br />
						<strong>Notes</strong> Note: A Certificate of Completion recognises course completion
						and hands-on practice; it is not a WSQ qualification. For WSQ e-Certs, see the WSQ
						Certificates tab.
					</Text>
					<Group justify="start" mt={10}>
						<ThemedButton
							onClick={() => {
								openContactUsForm();
							}}>
							Enquire Now
						</ThemedButton>
						<ThemedButton variant="outline" component={Link} to={AVAILABLE_COURSES_LINKS}>
							View Courses
						</ThemedButton>
					</Group>
				</Box>
			</Flex>
		</ThemedCard>
	);
};

const BundleCertEmptyPlaceholder = ({ previewUrl = CertExample }: { previewUrl?: string }) => {
	return (
		<ThemedCard px={30} mb={50} mt={20}>
			<Flex
				w="100%"
				direction={{ base: "column", md: "row" }}
				align="center"
				justify="center"
				my={20}
				gap={20}>
				<Flex flex={"0 1 45%"}>
					<CertFolderAnimation key={previewUrl} previewImage={previewUrl} />
				</Flex>
				<Box ml={{ base: 0, md: 10 }} flex="1 1 200px">
					<Title order={2} ta={{ base: "center", md: "left" }} mt={{ base: 20, md: 0 }} lh={1}>
						Professional Competency & Workplace Excellence Certificate
					</Title>
					<br />
					<Flex
						h="100%"
						align="center"
						bg="themed-black.0"
						px={10}
						py={10}
						pt={12}
						bdrs={5}
						gap={10}>
						<Text size="xs">
							<i
								className="fa fa-info-circle"
								style={{ color: "var(--mantine-color-themed-black-7)" }}
							/>
						</Text>
						<Text size="xs">You do not have any certificate at the moment.</Text>
					</Flex>
					<br />
					<Text size="sm" lh={1.3}>
						Complete 3 or more courses to receive a verifiable, personalised Certificate of
						Professional Competency & Workplace Excellence that best cater to your professional
						needs.
						<br />
						<br />
						Add the certificate into your resume to showcase your new skills sets to increase your
						employability or promotion opportunities.
						<br />
						<br />
						<strong>How to verify the Certificate</strong>
						<br />
						<br />
						<strong>Step 1:</strong> Scan QR code on the Certificate
						<br />
						<strong>Step 2:</strong> Verify the Certificate
						<br />
						<br />
					</Text>

					<Group justify="start" mt={10}>
						<ThemedButton
							onClick={() => {
								openContactUsForm();
							}}>
							Enquire Now
						</ThemedButton>
						<ThemedButton variant="outline" component={Link} to="/course/bundle/16">
							View Courses
						</ThemedButton>
					</Group>
				</Box>
			</Flex>
		</ThemedCard>
	);
};

// const CertFolderAnimation = () => {
// 	return (
// 		<div style={{
// 			display: 'flex', flexDirection: 'column', gap: 10, position: 'relative'
// 		}}>
// 			<GreenFolder width={100} height={'auto'} style={{ fill: '#00AB84', }} />
// 			<GreenFile width={100} height={'auto'} style={{ fill: '#00AB84', position: 'absolute', top: 0, left: 0 }} />
// 			<img
// 				style={{ aspectRatio: 0.73, width: 100, position: 'absolute', top: 0, left: 0 }}
// 				src={CertExample}
// 			/>
// 		</div>
// 	)
// }

const IndividualCertSkeleton = () => {
	return (
		<SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
			{Array.from({ length: 3 }).map((cert) => {
				return (
					<ThemedCard>
						<Skeleton enableAnimation={true} count={1} height={200} width={"100%"} />
						<Skeleton enableAnimation={true} count={3} height={15} width={"100%"} />
					</ThemedCard>
				);
			})}
		</SimpleGrid>
	);
};

const Banner = React.memo(() => {
	return (
		<div
			style={{
				width: "100%",
				// backgroundImage: `url("${CertListingImage}")`,
				// backgroundAttachment: "fixed",
				// backgroundPosition: "right -150px",
				// backgroundRepeat: "no-repeat",
				// backgroundSize: "auto min(100%, 1000px)",
			}}>
			<Box
				pos="relative"
				bg="linear-gradient(to right, rgba(0, 25, 50, 0.1) 0%, rgba(0, 243, 187, 0.4) 100%)"
				p={20}
				bdrs={10}>
				<GreenWaves />
				<Container py={{ base: 10, md: 20, lg: 50 }}>
					<Box
						display={"flex"}
						w={"100%"}
						px={{ base: 20, md: 0 }}
						py={50}
						style={{
							alignItems: "center",
						}}>
						<motion.div
							initial={{ opacity: 0, y: 50 }}
							whileInView={{ opacity: 1, y: 0 }}
							className={styles.cornerAccent}
							viewport={{ once: true }}
							transition={{
								duration: 1,
								ease: "easeOut",
							}}>
							<Title fz={40} ta="center">
								Professional Certifications
							</Title>
							<Text c="#003640" size={"lg"} lh={1.3} mt={15} ta="center">
								Access and download the certificates for your completed courses directly through
								this portal. These documents can be used for employment, further training, or
								personal record-keeping. Please ensure your information is accurate and up to date
								before downloading.
							</Text>
						</motion.div>
					</Box>
				</Container>
			</Box>
		</div>
	);
});
